// Client helper for /api/gemini/chat. Returns an async iterator that yields
// text fragments as Gemini streams them in.
//
// Usage:
//   for await (const chunk of streamChat({ messages, signal })) {
//     setDraft((d) => d + chunk);
//   }

export const DEFAULT_MODELS = [
  { id: 'gemini-2.5-flash-lite',  label: 'Gemini 2.5 Flash Lite', tier: 'fast' },
  { id: 'gemini-2.5-flash',       label: 'Gemini 2.5 Flash',      tier: 'balanced' },
  { id: 'gemini-2.5-pro',         label: 'Gemini 2.5 Pro',        tier: 'deep' },
];

export async function* streamChat({
  messages,
  model,
  system,
  temperature,
  maxOutputTokens,
  signal,
} = {}) {
  if (!Array.isArray(messages) || messages.length === 0) {
    throw new Error('streamChat: messages[] required');
  }

  const res = await fetch('/api/gemini/chat', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ messages, model, system, temperature, maxOutputTokens }),
    signal,
  });

  if (!res.ok || !res.body) {
    const errBody = await safeReadJson(res);
    const message = errBody?.error?.message || `gemini proxy ${res.status}`;
    const err = new Error(message);
    err.code = errBody?.error?.code ?? 'http_error';
    err.status = res.status;
    throw err;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });

      // SSE frames are separated by blank lines. Each frame can have multiple
      // `data:` lines that concatenate into one JSON payload (rare for
      // Gemini, but we handle it).
      let nl;
      while ((nl = buf.indexOf('\n\n')) !== -1) {
        const frame = buf.slice(0, nl);
        buf = buf.slice(nl + 2);
        const payload = parseSseFrame(frame);
        if (payload == null) continue;
        const text = extractText(payload);
        if (text) yield text;
      }
    }
    // Flush any tail frame that didn't terminate in \n\n.
    if (buf.trim().length > 0) {
      const payload = parseSseFrame(buf);
      const text = extractText(payload);
      if (text) yield text;
    }
  } finally {
    try { reader.releaseLock(); } catch {}
  }
}

function parseSseFrame(frame) {
  const dataLines = [];
  for (const line of frame.split('\n')) {
    if (line.startsWith('data:')) dataLines.push(line.slice(5).trimStart());
  }
  if (dataLines.length === 0) return null;
  const raw = dataLines.join('\n');
  if (raw === '[DONE]') return null;
  try { return JSON.parse(raw); } catch { return null; }
}

function extractText(payload) {
  if (!payload) return '';
  const parts = payload?.candidates?.[0]?.content?.parts;
  if (!Array.isArray(parts)) return '';
  let out = '';
  for (const p of parts) if (typeof p?.text === 'string') out += p.text;
  return out;
}

async function safeReadJson(res) {
  try { return await res.json(); } catch { return null; }
}
